import type {
  CmmdThreadArchive,
  PostCmmdThreadArchiveParams,
  ProjectId,
  ScopedThreadRef,
} from "@cmmd-forge/contracts";

import {
  cmmdRuntimeInventoryResource,
  findCmmdRuntimeInventoryThread,
} from "../cmmd/cmmdRuntimeInventoryResource";
import { resolveRuntimeIdForEnvironment } from "../cmmd/runtimeAvailabilityForEnvironment";
import { cmmdApiClient } from "../environments/cmmdApi";
import { submitThreadArchiveWithRevisionRetry } from "../environments/cmmdApi/threadArchive";
import { newCommandId } from "../lib/utils";

type ThreadArchiveOwnerAction = NonNullable<PostCmmdThreadArchiveParams["action"]>;

interface ArchiveThreadWithWorktreeReleaseInput {
  readonly threadRef: ScopedThreadRef;
  readonly projectId: ProjectId;
  readonly action: ThreadArchiveOwnerAction;
  readonly force?: boolean;
  readonly operationId?: string;
  readonly expectedRevision: number;
}

/**
 * Record the owner command in CMMD. The Environment performs cleanup through
 * its runtime archive reconciler and CMMD remains the only terminal state.
 */
export async function archiveThreadWithWorktreeRelease(
  input: ArchiveThreadWithWorktreeReleaseInput,
): Promise<CmmdThreadArchive> {
  const runtimeId = resolveRuntimeIdForEnvironment(input.threadRef.environmentId);
  const idempotencyKey = newCommandId();
  const result = await submitThreadArchiveWithRevisionRetry<CmmdThreadArchive>({
    action: input.action,
    ...(input.operationId === undefined ? {} : { operationId: input.operationId }),
    expectedRevision: input.expectedRevision,
    force: input.force ?? false,
    submit: (command) =>
      cmmdApiClient.postThreadArchive(runtimeId, input.projectId, input.threadRef.threadId, {
        ...command,
        idempotencyKey,
      }),
    refresh: async () => {
      const refreshed = await cmmdRuntimeInventoryResource.refresh({ allWorkspaces: true });
      return {
        revision:
          findCmmdRuntimeInventoryThread(
            refreshed,
            runtimeId,
            input.projectId,
            input.threadRef.threadId,
          )?.archive?.revision ?? 0,
      };
    },
  });
  await cmmdRuntimeInventoryResource.refresh({ allWorkspaces: true }).catch(() => undefined);
  return result;
}
